import { z } from 'zod'

export const RiskDimensionConfigSchema = z.object({
  name: z.string(),
  label: z.string(),
  description: z.string(),
  weight: z.number(),
})

export type RiskDimensionConfig = z.infer<typeof RiskDimensionConfigSchema>

export const DEFAULT_INHERENT_RISK_DIMENSIONS: RiskDimensionConfig[] = [
  {
    name: 'data_sensitivity',
    label: 'Data Sensitivity',
    description:
      'What types of data will the vendor access, store, or process on behalf of the organisation? Consider whether the data includes personally identifiable information (PII), financial records, health or medical data, confidential intellectual property, employee data, or sensitive commercial information. Assess whether the vendor will have access to production data or only anonymised/test data. Higher sensitivity data classifications (e.g. confidential, restricted) and broader data categories increase risk significantly.',
    weight: 0.25,
  },
  {
    name: 'system_access_level',
    label: 'System Access Level',
    description:
      'What level of access will the vendor require to internal systems, networks, or infrastructure? Consider whether access is read-only or includes write/modify/delete capabilities, whether they will access production environments or only lower environments, whether admin or privileged access is required, and whether they will connect to internal networks via VPN or direct integration. Vendors with broad, privileged, or unmonitored access represent a materially higher risk than those with narrow, scoped, or read-only access.',
    weight: 0.2,
  },
  {
    name: 'regulatory_scope',
    label: 'Regulatory Scope',
    description:
      'Is this engagement subject to regulatory, legal, or compliance requirements that impose obligations on how data is handled or how the vendor must operate? Consider frameworks such as GDPR, UK GDPR, HIPAA, PCI-DSS, SOC 2, ISO 27001, FCA regulations, or sector-specific rules. Also consider whether the engagement involves data subjects in regulated jurisdictions, or whether it touches systems or processes that are in scope for internal audit or external certification. Engagements with multiple overlapping regulatory obligations carry higher risk.',
    weight: 0.15,
  },
  {
    name: 'business_criticality',
    label: 'Business Criticality',
    description:
      'How critical is this vendor to the continuity of business operations? Consider whether the vendor provides a service that, if unavailable, would halt or severely degrade core business processes, revenue generation, or customer-facing services. Assess the availability of alternative vendors or internal fallback options, and how quickly the organisation could recover from a vendor failure. Vendors that are deeply embedded in critical workflows with no short-term substitute represent higher risk than discretionary or easily replaceable services.',
    weight: 0.15,
  },
  {
    name: 'data_volume',
    label: 'Data Volume',
    description:
      'How much data will the vendor handle, transfer, or process over the course of this engagement? Consider both the volume of records (e.g. number of individuals or transactions) and the frequency of data flows (e.g. real-time streaming vs. one-off batch transfer). Large-scale data processing amplifies the impact of any breach or misuse. Also consider whether the engagement involves bulk exports, data warehousing, or analytics over large datasets, as these increase exposure compared to narrow, record-level access.',
    weight: 0.1,
  },
  {
    name: 'engagement_type',
    label: 'Engagement Type',
    description:
      'What is the nature and structure of this vendor engagement? Consider whether it is a software-as-a-service (SaaS) product, a professional services arrangement, outsourced operations, a one-off project, or an embedded staffing model. SaaS and outsourced operations typically involve ongoing data processing obligations and higher inherent risk than time-limited projects. Also consider whether the vendor will sub-process data through third parties, and whether the engagement involves human access to data vs. automated processing only.',
    weight: 0.1,
  },
  {
    name: 'geographic_risk',
    label: 'Geographic Risk',
    description:
      "Where is the vendor headquartered, and where will data be stored or processed? Consider whether the vendor operates in jurisdictions with adequate data protection laws (e.g. EU adequacy decisions), or in countries flagged as high-risk by regulators or internal policy. Assess whether data will be transferred across borders, whether the vendor uses sub-processors in multiple regions, and whether there are geopolitical or sanctions-related risks associated with the vendor's country of operation. Vendors outside of recognised adequacy territories require additional safeguards.",
    weight: 0.05,
  },
]
